<template>
  <div class="ma-2">
    <v-row>
      <v-col cols="2">
        <v-tooltip bottom :open-delay="500">
          <template v-slot:activator="{ on, attrs }">
            <v-combobox
              v-on="on"
              v-bind="attrs"
              v-model="editLang.langName"
              :items="langs"
              item-text="name"
              item-value="code"
              :label="$t('label.language')"
              hide-details
              v-on:change="prepareNewLanguage"
            />
          </template>
          <span v-html="langsTooltip"></span>
        </v-tooltip>
      </v-col>
      <v-col cols="2">
        <v-text-field v-model="editLang.langID" :disabled="lockLangID" :label="$t('label.languageCode')" hide-details />
      </v-col>
      <v-col cols="2">
        <v-text-field v-model="editLang.langLocalName" :label="$t('label.languageLocalName')" hide-details />
      </v-col>
      <v-col cols="2">
        <v-autocomplete
          v-model="editLang.flag"
          :items="countries"
          item-text="czShort"
          item-value="flag"
          :label="$t('label.flag')"
          hide-details
          deletable
        ></v-autocomplete>
      </v-col>
      <v-col cols="1">
        <v-btn
          color="colorSave"
          @click="saveLanguage"
          :disabled="!$store.state.auth.userSettings.accEmployees.update || !$store.state.auth.userSettings.accEmployees.write"
          class="mt-2"
          >{{ $t('btn.save') }}</v-btn
        >
      </v-col>
      <v-col cols="1">
        <v-btn
          color="colorDelete"
          @click="deleteLanguage"
          :disabled="!$store.state.auth.userSettings.accEmployees.delete"
          class="mt-2 ml-5"
          >{{ $t('btn.delete') }}</v-btn
        >
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-autocomplete
          v-model="editLang.countries"
          :items="countries"
          item-text="czShort"
          item-value="A3"
          :label="$tc('label.country', 2)"
          multiple
          hide-details
          deletable-chips
          dense
          small-chips
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-divider class="mt-3"></v-divider>

    <v-sheet :style="`height: ${$store.state.windowSize.y - 180}px; overflow-x: hidden; overflow-y: auto`">
      <v-row>
        <v-col cols="5">
          <div v-for="(btn, index) in selectTooltipLanguage().ld.btn" :key="index">
            <v-row>
              <v-col cols="3">
                <v-tooltip bottom :open-delay="500">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs" class="pt-2">
                      <b>{{ index }}:</b>
                    </div>
                  </template>
                  <span>{{ btn }}</span>
                </v-tooltip>
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model="editLang.ld.btn[index]"
                  :disabled="checkEditDisabled(signTermLangs[editLang.langID])"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col cols="6">
          <div v-for="(msg, index) in selectTooltipLanguage().ld.msg" :key="index">
            <v-row>
              <v-col cols="3">
                <v-tooltip bottom :open-delay="500">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs" class="pt-2">
                      <b>{{ index }}:</b>
                    </div>
                  </template>
                  <span>{{ msg }}</span>
                </v-tooltip>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  v-model="editLang.ld.msg[index]"
                  :disabled="checkEditDisabled(signTermLangs[editLang.langID])"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-sheet>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';

export default Vue.extend({
  name: 'SettingsSignTerm',
  data: () => ({
    signTermLangs: { en: { ld: { btn: {}, msg: {} } } } as any,
    selectedLang: { name: '', code: '' },
    editLang: {
      langID: '',
      langName: '',
      langLocalName: '',
      ld: { btn: {}, msg: {} } as any, // Language data
      countries: [] as any,
      flag: '',
      editDisabled: false,
    },
    langs: [{ name: '', code: '' }],
    langsTooltip: '',
    countries: [],
    lockLangID: false,
  }),
  mounted() {
    this.listCountriesLoad();
    this.listLangsLoad().then(() => {
      this.listSignTermLangsLoad();
    });
  },
  methods: {
    prepareNewLanguage(item: any) {
      // Empty lang
      if (item === null) {
        return;
      }
      if (typeof item === 'object') {
        this.editLang.langID = item.code;
        this.editLang.langName = item.name;
        this.lockLangID = true;
      } else {
        this.editLang.langID = '';
        this.lockLangID = false;
        this.langs.forEach((lang) => {
          // On Manual input langName may match existing lang (avoid this)
          if (lang.name.toLowerCase() === item.toLowerCase()) {
            this.editLang.langName = lang.name;
            this.editLang.langID = lang.code;
            this.lockLangID = true;
          }
        });
      }
      if (this.signTermLangs[item.code] === undefined) {
        this.editLang.ld = { btn: {}, msg: {} };
        this.editLang.countries = [];
        this.editLang.langLocalName = '';
        this.editLang.flag = '';
        this.editLang.editDisabled = false;
      } else {
        this.editLang = { ...this.signTermLangs[item.code] };
      }
    },
    async listLangsLoad() {
      await axios
        .get('/api/v1/languagesall')
        .then((response) => {
          this.langs = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    checkEditDisabled(lang: { editDisabled: boolean }) {
      let disabled = false;
      lang === undefined ? (disabled = false) : (disabled = lang.editDisabled);
      return disabled;
    },
    selectTooltipLanguage() {
      let lang = { langID: '' } as any;
      for (lang of Object.entries(this.signTermLangs)) {
        if (lang[1].langID === this.$vuetify.lang.current) {
          return lang[1];
        }
      }
      return this.signTermLangs.en;
    },
    listSignTermLangsLoad() {
      axios
        .get('/api/v1/signtermlangs')
        .then((response) => {
          let found = false;
          this.signTermLangs = response.data;
          let termLang = { langID: '' } as any;
          this.langsTooltip = '';
          this.langsTooltip += this.$i18n.t('label.languagesExisting') + `:<br>`;
          // append user defined languages to lang selection
          for (termLang of Object.entries(this.signTermLangs)) {
            this.langsTooltip += termLang[1].langName + `<br>`;
            // termLang[1].editDisabled === undefined ? (termLang[1].editDisabled = false) : null;
            this.langs.every((lang) => {
              if (termLang[1].langID === lang.code) {
                found = true;
                return false;
              }
              return true;
            });
            if (!found) {
              this.langs.push({ name: termLang[1].langName, code: termLang[1].langID });
            }
            found = false;
          }
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    listCountriesLoad() {
      axios
        .get(`/api/v1/countries`)
        .then((response) => {
          for (const country of response.data) {
            country.czShort = country.flag + ' - ' + country.czShort + ' - ' + country.A3;
          }
          this.countries = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    saveLanguage() {
      this.langs.forEach((lang) => {
        if (lang.code === this.editLang.langID && lang.name !== this.editLang.langName) {
          this.$store.commit('globalMessage', 'Language code already exists');
          return;
        }
      });
      if (this.editLang.langID.length !== 2) {
        this.$store.commit('globalMessage', 'Language code must have 2 characters');
        return;
      }
      axios
        .post('/api/v1/signtermlangs', this.editLang)
        .then(() => {
          this.listLangsLoad().then(() => {
            this.listSignTermLangsLoad();
          });
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    deleteLanguage() {
      axios
        .delete(`/api/v1/signtermlangs/${this.editLang.langID}`)
        .then(() => {
          this.listLangsLoad().then(() => {
            this.listSignTermLangsLoad();
          });
          this.editLang = {
            langID: '',
            langName: '',
            langLocalName: '',
            ld: { btn: {}, msg: {} } as any, // Language data
            countries: [] as any,
            flag: '',
            editDisabled: false,
          };
          this.prepareNewLanguage(null);
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
  },
});
</script>
